import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiOutlineCalendar, HiOutlineLocationMarker, HiOutlineClock, HiCheck, HiX } from 'react-icons/hi';
import { conferencesService } from '../../services/conferencesService';
import { speakersService } from '../../services/speakersService';
import { cadreConferenceService } from '../../services/cadreConferenceService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import ExpandableText from './ExpandableText';

function ConferencesManagement({ selectedEdition }) {
  const [conferencesData, setConferencesData] = useState({
    conferences: [],
    salles: [],
    statutOptions: [
      { value: 'en_attente', label: 'En Attente', color: '#ffc107' },
      { value: 'confirmee', label: 'Confirmée', color: '#28a745' },
      { value: 'annulee', label: 'Annulée', color: '#dc3545' }
    ]
  });
  
  const [editingConference, setEditingConference] = useState(null);
  const [showAddConferenceForm, setShowAddConferenceForm] = useState(false);
  const [showSalleManagement, setShowSalleManagement] = useState(false);
  const [showCadreManagement, setShowCadreManagement] = useState(false);
  const [newConference, setNewConference] = useState({
    titre: '',
    dateDebut: '',
    heureDebut: '',
    dateFin: '',
    heureFin: '',
    salleId: '',
    intervenant: '',
    description: '',
    statut: 'en_attente',
    speakers: [] // array of speaker IDs
  });
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreDate, setFiltreDate] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [speakersList, setSpeakersList] = useState([]);
  const [showEditConferenceModal, setShowEditConferenceModal] = useState(false);
  const [showSpeakersModal, setShowSpeakersModal] = useState(false);
  const [modalSpeakers, setModalSpeakers] = useState([]);
  const [cadresList, setCadresList] = useState([]);

  useEffect(() => {
    if (selectedEdition) {
      loadConferencesData();
      loadSpeakersList();
      loadCadresList();
    }
  }, [selectedEdition]);

  const loadConferencesData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading conferences data for edition:', selectedEdition);
      const data = await conferencesService.getConferencesData(selectedEdition);
      
      if (!data || ((!data.conferences || data.conferences.length === 0) && 
                    (!data.salles || data.salles.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          conferences: [],
          salles: [],
          statutOptions: [
            { value: 'en_attente', label: 'En Attente', color: '#ffc107' },
            { value: 'confirmee', label: 'Confirmée', color: '#28a745' },
            { value: 'annulee', label: 'Annulée', color: '#dc3545' }
          ]
        };
        setConferencesData(defaultData);
      } else {
        console.log('Data found:', data);
        setConferencesData(data);
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading conferences data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        conferences: [],
        salles: [],
        statutOptions: [
          { value: 'en_attente', label: 'En Attente', color: '#ffc107' },
          { value: 'confirmee', label: 'Confirmée', color: '#28a745' },
          { value: 'annulee', label: 'Annulée', color: '#dc3545' }
        ]
      };
      setConferencesData(defaultData);
    } finally {
      setLoading(false);
    }
  };

  const loadSpeakersList = async () => {
    try {
      const data = await speakersService.getSpeakersData(selectedEdition);
      // Merge both regular and online speakers for selection
      const allSpeakers = [
        ...(data?.speakers || []),
        ...(data?.onlineSpeakers || [])
      ];
      setSpeakersList(allSpeakers);
    } catch (e) {
      setSpeakersList([]);
    }
  };

  const loadCadresList = async () => {
    try {
      const cadres = await cadreConferenceService.getCadresData(selectedEdition);
      setCadresList(cadres);
    } catch (e) {
      setCadresList([]);
    }
  };

  const handleEditConference = (conference) => {
    setEditingConference({
      ...conference,
      salleId: conference.salleId || '',
      speakers: conference.speakers || []
    });
    setShowEditConferenceModal(true);
  };

  const handleDeleteConference = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette conférence ?')) {
      try {
        await conferencesService.deleteConference(id);
        setConferencesData(prev => ({
          ...prev,
          conferences: prev.conferences.filter(conf => conf.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting conference:', error);
        alert(error.message || 'Erreur lors de la suppression de la conférence.');
      }
    }
  };

  const handleSaveConference = async (updatedConference) => {
    try {
      const updatedData = await conferencesService.updateConference(updatedConference.id, {
        ...updatedConference,
        salleId: updatedConference.salleId || null,
        speakers: updatedConference.speakers || []
      });
      const formattedConference = {
        id: updatedData.id,
        titre: updatedData.titre,
        dateDebut: updatedData.date_debut,
        heureDebut: updatedData.heure_debut,
        dateFin: updatedData.date_fin,
        heureFin: updatedData.heure_fin,
        salle: updatedData.conferences_salles?.nom_salle || '',
        salleId: updatedData.salle_id,
        intervenant: updatedData.intervenant,
        description: updatedData.description,
        statut: updatedData.statut,
        speakers: updatedData.speakers || []
      };
      setConferencesData(prev => ({
        ...prev,
        conferences: prev.conferences.map(conf =>
          conf.id === updatedConference.id ? formattedConference : conf
        )
      }));
      setEditingConference(null);
      setShowEditConferenceModal(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating conference:', error);
      alert('Erreur lors de la modification de la conférence.');
    }
  };

  const handleAddConference = async () => {
    try {
      const newConferenceFromDb = await conferencesService.addConference({
        ...newConference,
        salleId: newConference.salleId || null,
        speakers: newConference.speakers || []
      }, selectedEdition);
      const formattedConference = {
        id: newConferenceFromDb.id,
        titre: newConferenceFromDb.titre,
        dateDebut: newConferenceFromDb.date_debut,
        heureDebut: newConferenceFromDb.heure_debut,
        dateFin: newConferenceFromDb.date_fin,
        heureFin: newConferenceFromDb.heure_fin,
        salle: newConferenceFromDb.conferences_salles?.nom_salle || '',
        salleId: newConferenceFromDb.salle_id,
        intervenant: newConferenceFromDb.intervenant,
        description: newConferenceFromDb.description,
        statut: newConferenceFromDb.statut,
        speakers: newConferenceFromDb.speakers || []
      };
      setConferencesData(prev => ({
        ...prev,
        conferences: [...prev.conferences, formattedConference]
      }));
      setNewConference({
        titre: '',
        dateDebut: '',
        heureDebut: '',
        dateFin: '',
        heureFin: '',
        salleId: '',
        intervenant: '',
        description: '',
        statut: 'en_attente',
        speakers: []
      });
      setShowAddConferenceForm(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error adding conference:', error);
      alert('Erreur lors de l\'ajout de la conférence.');
    }
  };

  const getConferencesFiltrees = () => {
    let filtered = conferencesData.conferences;
    
    if (filtreStatut !== 'tous') {
      filtered = filtered.filter(conf => conf.statut === filtreStatut);
    }
    
    if (filtreDate) {
      filtered = filtered.filter(conf => conf.dateDebut === filtreDate);
    }
    
    return filtered.sort((a, b) => {
      const dateA = new Date(a.dateDebut + ' ' + a.heureDebut);
      const dateB = new Date(b.dateDebut + ' ' + b.heureDebut);
      return dateA - dateB;
    });
  };

  const getStatutInfo = (statut) => {
    return conferencesData.statutOptions.find(option => option.value === statut);
  };

  const formatDateTime = (date, heure) => {
    if (!date || !heure) return '';
    const dateObj = new Date(date + ' ' + heure);
    return dateObj.toLocaleDateString('fr-FR') + ' à ' + heure;
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  // Helper: get salle capacity by salleId
  const getSalleCapacity = (salleId) => {
    const salle = conferencesData.salles.find(s => String(s.id) === String(salleId));
    return salle ? salle.capacite || 0 : 0;
  };

  // Helper: count confirmed participants for a conference
  const getConfirmedParticipantsCount = (conferenceId) => {
    // You may want to fetch visiteurs from a service, but for simplicity, assume speakersList contains visiteurs with conferenceId and statut
    // In real code, fetch visiteurs for selectedEdition and count those with conferenceId and statut === 'email confirme'
    // Here, we add a placeholder for this logic
    // TODO: Replace with actual visiteursService.getVisiteurs(selectedEdition)
    return 0; // Placeholder, must be replaced with actual count
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Conferences est vide pour cette édition. Commencez par créer des salles, puis ajoutez des conférences.</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Opération effectuée avec succès !
        </div>
      )}

      {/* Toggle between Conference Management, Salle Management, and Cadre Management */}
      <div className="section-header">
        <h3>Gestion des {showCadreManagement ? 'Cadres de Conférence' : showSalleManagement ? 'Salles' : 'Conférences'}</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn-secondary" 
            onClick={() => {
              setShowCadreManagement(false);
              setShowSalleManagement(!showSalleManagement);
            }}
          >
            {showSalleManagement ? 'Gérer les Conférences' : 'Gérer les Salles'}
          </button>
          <button 
            className="btn-secondary" 
            onClick={() => {
              setShowSalleManagement(false);
              setShowCadreManagement(!showCadreManagement);
            }}
          >
            {showCadreManagement ? 'Gérer les Conférences' : 'Gérer les Cadres'}
          </button>
          {!showSalleManagement && !showCadreManagement && (
            <button className="add-btn" onClick={() => setShowAddConferenceForm(true)}>
              <HiPlus /> Ajouter une conférence
            </button>
          )}
        </div>
      </div>

      {showCadreManagement ? (
        <CadreManagement 
          cadres={cadresList}
          selectedEdition={selectedEdition}
          onCadreAdded={(newCadre) => {
            setCadresList(prev => [...prev, newCadre]);
            showSuccessMessage();
          }}
          onCadreUpdated={(updatedCadre) => {
            setCadresList(prev => prev.map(cadre =>
              cadre.id === updatedCadre.id ? updatedCadre : cadre
            ));
            showSuccessMessage();
          }}
          onCadreDeleted={(cadreId) => {
            setCadresList(prev => prev.filter(cadre => cadre.id !== cadreId));
            showSuccessMessage();
          }}
        />
      ) : showSalleManagement ? (
        <SalleManagement 
          salles={conferencesData.salles}
          selectedEdition={selectedEdition}
          onSalleAdded={(newSalle) => {
            const formattedSalle = {
              id: newSalle.id,
              nom: newSalle.nom_salle,
              capacite: newSalle.capacite,
              equipements: newSalle.equipements
            };
            setConferencesData(prev => ({
              ...prev,
              salles: [...prev.salles, formattedSalle]
            }));
            showSuccessMessage();
          }}
          onSalleUpdated={(updatedSalle) => {
            const formattedSalle = {
              id: updatedSalle.id,
              nom: updatedSalle.nom_salle,
              capacite: updatedSalle.capacite,
              equipements: updatedSalle.equipements
            };
            setConferencesData(prev => ({
              ...prev,
              salles: prev.salles.map(salle =>
                salle.id === updatedSalle.id ? formattedSalle : salle
              )
            }));
            showSuccessMessage();
          }}
          onSalleDeleted={(salleId) => {
            setConferencesData(prev => ({
              ...prev,
              salles: prev.salles.filter(salle => salle.id !== salleId)
            }));
            showSuccessMessage();
          }}
        />
      ) : (
        <>
          {/* Filtres et Actions */}
          <div className="filter-controls">
            <div className="form-group">
              <label>Filtrer par statut:</label>
              <select 
                value={filtreStatut}
                onChange={(e) => setFiltreStatut(e.target.value)}
              >
                <option value="tous">Tous les statuts</option>
                {conferencesData.statutOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Filtrer par date:</label>
              <input 
                type="date" 
                value={filtreDate}
                onChange={(e) => setFiltreDate(e.target.value)}
              />
            </div>
          </div>

          {/* Add Conference Form */}
          {showAddConferenceForm && (
            <div className="add-form">
              <div className="form-group">
                <label>Titre:</label>
                <input 
                  type="text" 
                  value={newConference.titre}
                  onChange={(e) => setNewConference({...newConference, titre: e.target.value})}
                  required
                />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Date de début:</label>
                  <input 
                    type="date" 
                    value={newConference.dateDebut}
                    onChange={(e) => setNewConference({...newConference, dateDebut: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure de début:</label>
                  <input 
                    type="time" 
                    value={newConference.heureDebut}
                    onChange={(e) => setNewConference({...newConference, heureDebut: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Date de fin:</label>
                  <input 
                    type="date" 
                    value={newConference.dateFin}
                    onChange={(e) => setNewConference({...newConference, dateFin: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure de fin:</label>
                  <input 
                    type="time" 
                    value={newConference.heureFin}
                    onChange={(e) => setNewConference({...newConference, heureFin: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Salle:</label>
                <select 
                  value={newConference.salleId}
                  onChange={(e) => setNewConference({...newConference, salleId: e.target.value})}
                >
                  <option value="">Sélectionner une salle</option>
                  {conferencesData.salles.map(salle => (
                    <option key={salle.id} value={salle.id}>{salle.nom}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Intervenant:</label>
                <input 
                  type="text" 
                  value={newConference.intervenant}
                  onChange={(e) => setNewConference({...newConference, intervenant: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Speakers liés:</label>
                <select
                  multiple
                  value={newConference.speakers}
                  onChange={e => {
                    const options = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setNewConference({ ...newConference, speakers: options });
                  }}
                  style={{ minHeight: '60px' }}
                >
                  {speakersList.map(speaker => (
                    <option key={speaker.id} value={speaker.id}>
                      {speaker.name} {speaker.title ? `(${speaker.title})` : ''}
                    </option>
                  ))}
                </select>
                <small>Maintenez Ctrl (Cmd sur Mac) pour sélectionner plusieurs intervenants.</small>
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea 
                  value={newConference.description}
                  onChange={(e) => setNewConference({...newConference, description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Statut:</label>
                <select 
                  value={newConference.statut}
                  onChange={(e) => setNewConference({...newConference, statut: e.target.value})}
                >
                  {conferencesData.statutOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button onClick={handleAddConference} className="btn-primary">
                  <HiPlus /> Ajouter
                </button>
                <button onClick={() => setShowAddConferenceForm(false)} className="btn-secondary">
                  Annuler
                </button>
              </div>
            </div>
          )}

          <div className="card">
            <DataTable 
              value={getConferencesFiltrees()} 
              paginator 
              rows={15}
              className="p-datatable-sm"
              stripedRows
              showGridlines
              responsiveLayout="scroll"
              scrollable
              tableStyle={{ tableLayout: 'auto' }}
              emptyMessage="Aucune conférence trouvée."
            >
              <Column 
                field="titre" 
                header="Titre" 
                sortable 
                body={(rowData) => <ExpandableText text={rowData.titre} maxChars={50} />}
                style={{ minWidth: '200px' }}
              />
              <Column 
                header="Date/Heure" 
                body={(rowData) => (
                  <div style={{ fontSize: '0.9em' }}>
                    <div>{formatDateTime(rowData.dateDebut, rowData.heureDebut)}</div>
                    <div style={{ color: '#666' }}>→ {formatDateTime(rowData.dateFin, rowData.heureFin)}</div>
                  </div>
                )}
                sortable
                sortField="dateDebut"
                style={{ whiteSpace: 'nowrap' }}
              />
              <Column 
                field="salle" 
                header="Salle" 
                sortable 
                body={(rowData) => {
                  const salleCapacity = getSalleCapacity(rowData.salleId);
                  const confirmedCount = 0; // Placeholder
                  return (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {rowData.salle || 'Aucune'}
                      {salleCapacity > 0 && (
                        <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>
                          ({confirmedCount}/{salleCapacity})
                        </span>
                      )}
                    </div>
                  );
                }}
                style={{ width: '1%' }}
              />
              <Column 
                field="intervenant" 
                header="Intervenant" 
                sortable 
                body={(rowData) => <ExpandableText text={rowData.intervenant} maxChars={40} />}
                style={{ minWidth: '150px' }}
              />
              <Column 
                header="Speakers liés" 
                body={(rowData) => {
                  const linkedSpeakers = (rowData.speakers || [])
                    .map(sid => speakersList.find(s => String(s.id) === String(sid)))
                    .filter(Boolean);
                  
                  return linkedSpeakers.length > 0 ? (
                    <div className="flex align-items-center gap-2">
                      <span style={{ fontSize: '0.9em' }}>{linkedSpeakers.length}</span>
                      <Button 
                        icon="pi pi-users" 
                        onClick={() => {
                          setModalSpeakers(linkedSpeakers);
                          setShowSpeakersModal(true);
                        }}
                        className="p-button-text p-button-sm p-0"
                        style={{ height: '24px', width: '24px' }}
                      />
                    </div>
                  ) : <span style={{ color: '#aaa' }}>Aucun</span>;
                }}
                style={{ width: '1%', whiteSpace: 'nowrap' }}
              />
              <Column 
                field="statut" 
                header="Statut" 
                body={(rowData) => (
                  <Tag 
                    value={getStatutInfo(rowData.statut)?.label || rowData.statut} 
                    severity={
                      rowData.statut === 'confirmee' ? 'success' : 
                      rowData.statut === 'annulee' ? 'danger' : 'warning'
                    }
                  />
                )}
                sortable
                style={{ width: '1%', whiteSpace: 'nowrap' }}
              />
              <Column 
                header="Actions" 
                body={(rowData) => (
                  <div className="flex gap-2">
                    <Button 
                      icon="pi pi-pencil" 
                      onClick={() => handleEditConference(rowData)} 
                      rounded 
                      outlined 
                      severity="info" 
                      size="small"
                    />
                    <Button 
                      icon="pi pi-trash" 
                      onClick={() => handleDeleteConference(rowData.id)} 
                      rounded 
                      outlined 
                      severity="danger" 
                      size="small"
                    />
                  </div>
                )}
                style={{ width: '1%', whiteSpace: 'nowrap' }}
              />
            </DataTable>
          </div>
          {/* Speakers Linked Modal */}
          {showSpeakersModal && (
            <SpeakersLinkedModal
              speakers={modalSpeakers}
              onClose={() => setShowSpeakersModal(false)}
            />
          )}
          {/* Edit Conference Modal */}
          {showEditConferenceModal && editingConference && (
            <ConferenceModal
              mode="edit"
              conference={editingConference}
              salles={conferencesData.salles}
              statutOptions={conferencesData.statutOptions}
              speakersList={speakersList}
              onSave={handleSaveConference}
              onClose={() => {
                setShowEditConferenceModal(false);
                setEditingConference(null);
              }}
            />
          )}
          {/* Add Conference Modal */}
          {showAddConferenceForm && (
            <ConferenceModal
              mode="add"
              conference={newConference}
              salles={conferencesData.salles}
              statutOptions={conferencesData.statutOptions}
              speakersList={speakersList}
              onSave={async (conf) => {
                setShowAddConferenceForm(false);
                await handleAddConferenceFromModal(conf);
              }}
              onClose={() => setShowAddConferenceForm(false)}
            />
          )}
        </>
      )}
    </div>
  );

  // Helper for add modal
  async function handleAddConferenceFromModal(conf) {
    try {
      const newConferenceFromDb = await conferencesService.addConference({
        ...conf,
        salleId: conf.salleId || null,
        speakers: conf.speakers || []
      }, selectedEdition);
      const formattedConference = {
        id: newConferenceFromDb.id,
        titre: newConferenceFromDb.titre,
        dateDebut: newConferenceFromDb.date_debut,
        heureDebut: newConferenceFromDb.heure_debut,
        dateFin: newConferenceFromDb.date_fin,
        heureFin: newConferenceFromDb.heure_fin,
        salle: newConferenceFromDb.conferences_salles?.nom_salle || '',
        salleId: newConferenceFromDb.salle_id,
        intervenant: newConferenceFromDb.intervenant,
        description: newConferenceFromDb.description,
        statut: newConferenceFromDb.statut,
        speakers: newConferenceFromDb.speakers || []
      };
      setConferencesData(prev => ({
        ...prev,
        conferences: [...prev.conferences, formattedConference]
      }));
      setNewConference({
        titre: '',
        dateDebut: '',
        heureDebut: '',
        dateFin: '',
        heureFin: '',
        salleId: '',
        intervenant: '',
        description: null,
        statut: 'en_attente',
        speakers: []
      });
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error adding conference:', error);
      alert('Erreur lors de l\'ajout de la conférence.');
    }
  }
}

// ConferenceModal component for add/edit
function ConferenceModal({ mode, conference, salles, statutOptions, speakersList, onSave, onClose }) {
  const [formData, setFormData] = useState(conference);

  useEffect(() => {
    setFormData(conference);
  }, [conference]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter une conférence' : 'Modifier la conférence'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-group">
              <label>Titre:</label>
              <input
                type="text"
                value={formData.titre}
                onChange={e => setFormData({ ...formData, titre: e.target.value })}
                required
              />
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Date de début:</label>
                <input
                  type="date"
                  value={formData.dateDebut}
                  onChange={e => setFormData({ ...formData, dateDebut: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Heure de début:</label>
                <input
                  type="time"
                  value={formData.heureDebut}
                  onChange={e => setFormData({ ...formData, heureDebut: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Date de fin:</label>
                <input
                  type="date"
                  value={formData.dateFin}
                  onChange={e => setFormData({ ...formData, dateFin: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Heure de fin:</label>
                <input
                  type="time"
                  value={formData.heureFin}
                  onChange={e => setFormData({ ...formData, heureFin: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Salle:</label>
              <select
                value={formData.salleId}
                onChange={e => setFormData({ ...formData, salleId: e.target.value })}
              >
                <option value="">Sélectionner une salle</option>
                {salles.map(salle => (
                  <option key={salle.id} value={salle.id}>{salle.nom}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Intervenant:</label>
              <input
                type="text"
                value={formData.intervenant}
                onChange={e => setFormData({ ...formData, intervenant: e.target.value })}
                // required
              />
            </div>
            <div className="form-group">
              <label>Speakers liés:</label>
              <select
                multiple
                value={formData.speakers || []}
                onChange={e => {
                  const options = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setFormData({ ...formData, speakers: options });
                }}
                style={{ minHeight: '60px' }}
              >
                {speakersList.map(speaker => (
                  <option key={speaker.id} value={speaker.id}>
                    {speaker.name} {speaker.title ? `(${speaker.title})` : ''}
                  </option>
                ))}
              </select>
              <small>Maintenez Ctrl (Cmd sur Mac) pour sélectionner plusieurs intervenants.</small>
            </div>
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Statut:</label>
              <select
                value={formData.statut}
                onChange={e => setFormData({ ...formData, statut: e.target.value })}
              >
                {statutOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {mode === 'add' ? 'Ajouter' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SalleManagement({ salles, selectedEdition, onSalleAdded, onSalleUpdated, onSalleDeleted }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSalle, setEditingSalle] = useState(null);
  const [newSalle, setNewSalle] = useState({
    nom: '',
    capacite: '',
    equipements: ''
  });

  const handleAddSalle = async () => {
    try {
      const newSalleFromDb = await conferencesService.addSalle(newSalle, selectedEdition);
      onSalleAdded(newSalleFromDb);
      setNewSalle({ nom: '', capacite: '', equipements: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding salle:', error);
      alert('Erreur lors de l\'ajout de la salle.');
    }
  };

  const handleUpdateSalle = async (salleData) => {
    try {
      const updatedSalle = await conferencesService.updateSalle(salleData.id, salleData);
      onSalleUpdated(updatedSalle);
      setEditingSalle(null);
    } catch (error) {
      console.error('Error updating salle:', error);
      alert('Erreur lors de la modification de la salle.');
    }
  };

  const handleDeleteSalle = async (salleId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette salle ?')) {
      try {
        await conferencesService.deleteSalle(salleId);
        onSalleDeleted(salleId);
      } catch (error) {
        console.error('Error deleting salle:', error);
        alert(error.message || 'Erreur lors de la suppression de la salle.');
      }
    }
  };

  return (
    <>
      <div className="section-header" style={{ marginTop: 0 }}>
        <h4>Salles de Conférence</h4>
        <button className="add-btn" onClick={() => setShowAddForm(true)}>
          <HiPlus /> Ajouter une salle
        </button>
      </div>

      {showAddForm && (
        <div className="add-form">
          <div className="form-group">
            <label>Nom de la salle:</label>
            <input 
              type="text" 
              value={newSalle.nom}
              onChange={(e) => setNewSalle({...newSalle, nom: e.target.value})}
              placeholder="Ex: Salle A, Amphithéâtre..."
              required
            />
          </div>
          <div className="form-group">
            <label>Capacité:</label>
            <input 
              type="number" 
              value={newSalle.capacite}
              onChange={(e) => setNewSalle({...newSalle, capacite: parseInt(e.target.value)})}
              placeholder="Nombre de places"
            />
          </div>
          <div className="form-group">
            <label>Équipements:</label>
            <textarea 
              value={newSalle.equipements}
              onChange={(e) => setNewSalle({...newSalle, equipements: e.target.value})}
              placeholder="Projecteur, micros, tableau..."
              rows="3"
            />
          </div>
          <div className="form-actions">
            <button onClick={handleAddSalle} className="btn-primary">
              <HiPlus /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn-secondary">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="data-table">
        <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 3fr 1fr', gap: '20px' }}>
          <div>Nom</div>
          <div>Capacité</div>
          <div>Équipements</div>
          <div>Actions</div>
        </div>
        {salles.map(salle => (
          <div key={salle.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 3fr 1fr', gap: '20px' }}>
            {editingSalle?.id === salle.id ? (
              <EditSalleRow 
                salle={editingSalle}
                onSave={handleUpdateSalle}
                onCancel={() => setEditingSalle(null)}
              />
            ) : (
              <>
                <div>{salle.nom}</div>
                <div>{salle.capacite || 'N/A'} places</div>
                <div>{salle.equipements || 'Aucun'}</div>
                <div className="actions">
                  <button onClick={() => setEditingSalle(salle)} className="edit-btn">
                    <HiPencil />
                  </button>
                  <button onClick={() => handleDeleteSalle(salle.id)} className="delete-btn">
                    <HiTrash />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function EditSalleRow({ salle, onSave, onCancel }) {
  const [editData, setEditData] = useState(salle);

  return (
    <>
      <div>
        <input 
          type="text" 
          value={editData.nom}
          onChange={(e) => setEditData({...editData, nom: e.target.value})}
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <input 
          type="number" 
          value={editData.capacite || ''}
          onChange={(e) => setEditData({...editData, capacite: parseInt(e.target.value) || null})}
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <textarea 
          value={editData.equipements || ''}
          onChange={(e) => setEditData({...editData, equipements: e.target.value})}
          style={{ width: '100%', minHeight: '40px' }}
        />
      </div>
      <div className="actions">
        <button onClick={() => onSave(editData)} className="btn-primary">
          <HiCheck />
        </button>
        <button onClick={onCancel} className="btn-secondary">
          <HiX />
        </button>
      </div>
    </>
  );
}

// Add CadreManagement component with better ordering
function CadreManagement({ cadres, selectedEdition, onCadreAdded, onCadreUpdated, onCadreDeleted }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCadre, setEditingCadre] = useState(null);
  const [showSpeakersOrderModal, setShowSpeakersOrderModal] = useState(false);
  const [selectedCadreForOrdering, setSelectedCadreForOrdering] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [tempCadresOrder, setTempCadresOrder] = useState([]);
  const [newCadre, setNewCadre] = useState({
    title: ''
  });

  const handleAddCadre = async () => {
    try {
      const newCadreFromDb = await cadreConferenceService.addCadre(newCadre, selectedEdition);
      onCadreAdded(newCadreFromDb);
      setNewCadre({ title: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding cadre:', error);
      alert('Erreur lors de l\'ajout du cadre.');
    }
  };

  const handleUpdateCadre = async (cadreData) => {
    try {
      const updatedCadre = await cadreConferenceService.updateCadre(cadreData.id, cadreData);
      onCadreUpdated(updatedCadre);
      setEditingCadre(null);
    } catch (error) {
      console.error('Error updating cadre:', error);
      alert('Erreur lors de la modification du cadre.');
    }
  };

  const handleDeleteCadre = async (cadreId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce cadre ?')) {
      try {
        await cadreConferenceService.deleteCadre(cadreId);
        onCadreDeleted(cadreId);
      } catch (error) {
        console.error('Error deleting cadre:', error);
        alert(error.message || 'Erreur lors de la suppression du cadre.');
      }
    }
  };

  const handleReorderCadres = () => {
    setTempCadresOrder(cadres.map((cadre, index) => ({
      id: cadre.id,
      title: cadre.title,
      order: cadre.display_order || (index + 1)
    })));
    setShowOrderModal(true);
  };

  const saveCadreOrder = async () => {
    try {
      const cadreOrders = tempCadresOrder.map((cadre, index) => ({
        id: cadre.id,
        order: index + 1
      }));

      await cadreConferenceService.reorderCadres(cadreOrders, selectedEdition);
      
      // Update local state
      const updatedCadres = cadres.map(cadre => {
        const newOrder = cadreOrders.find(co => co.id === cadre.id);
        return { ...cadre, display_order: newOrder ? newOrder.order : cadre.display_order };
      }).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

      updatedCadres.forEach(cadre => onCadreUpdated(cadre));
      setShowOrderModal(false);
    } catch (error) {
      console.error('Error saving cadre order:', error);
      alert('Erreur lors de la sauvegarde de l\'ordre des cadres.');
    }
  };

  const moveCadreUp = (index) => {
    if (index === 0) return;
    const newOrder = [...tempCadresOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setTempCadresOrder(newOrder);
  };

  const moveCadreDown = (index) => {
    if (index === tempCadresOrder.length - 1) return;
    const newOrder = [...tempCadresOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setTempCadresOrder(newOrder);
  };

  const updateCadreOrder = (id, newOrder) => {
    setTempCadresOrder(prev => 
      prev.map(cadre => 
        cadre.id === id 
          ? { ...cadre, order: Math.max(1, Math.min(prev.length, parseInt(newOrder) || 1)) }
          : cadre
      ).sort((a, b) => a.order - b.order)
    );
  };

  const handleManageSpeakersOrder = (cadre) => {
    setSelectedCadreForOrdering(cadre);
    setShowSpeakersOrderModal(true);
  };

  return (
    <>
      <div className="section-header" style={{ marginTop: 0 }}>
        <h4>Cadres de Conférence</h4>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="add-btn" onClick={() => setShowAddForm(true)}>
            <HiPlus /> Ajouter un cadre
          </button>
          {cadres.length > 1 && (
            <button className="btn-secondary" onClick={handleReorderCadres}>
              <HiPencil /> Réorganiser l'ordre
            </button>
          )}
        </div>
      </div>

      {showAddForm && (
        <div className="add-form">
          <div className="form-group">
            <label>Titre du cadre:</label>
            <input 
              type="text" 
              value={newCadre.title}
              onChange={(e) => setNewCadre({...newCadre, title: e.target.value})}
              placeholder="Ex: Président de séance, Modérateur..."
              required
            />
          </div>
          <div className="form-actions">
            <button onClick={handleAddCadre} className="btn-primary">
              <HiPlus /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn-secondary">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="data-table">
        <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '1fr 3fr 1fr 1fr', gap: '20px' }}>
          <div>Ordre</div>
          <div>Titre</div>
          <div>Speakers</div>
          <div>Actions</div>
        </div>
        {cadres.map((cadre, index) => (
          <div key={cadre.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '1fr 3fr 1fr 1fr', gap: '20px' }}>
            {editingCadre?.id === cadre.id ? (
              <EditCadreRow 
                cadre={editingCadre}
                onSave={handleUpdateCadre}
                onCancel={() => setEditingCadre(null)}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#f0f0f0', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    minWidth: '30px',
                    textAlign: 'center'
                  }}>
                    {cadre.display_order || index + 1}
                  </span>
                </div>
                <div>{cadre.title}</div>
                <div>
                  <button
                    className="btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    onClick={() => handleManageSpeakersOrder(cadre)}
                  >
                    Gérer ordre
                  </button>
                </div>
                <div className="actions">
                  <button onClick={() => setEditingCadre(cadre)} className="edit-btn">
                    <HiPencil />
                  </button>
                  <button onClick={() => handleDeleteCadre(cadre.id)} className="delete-btn">
                    <HiTrash />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Order Management Modal */}
      {showOrderModal && (
        <CadreOrderModal
          cadres={tempCadresOrder}
          onCadreOrderChange={setTempCadresOrder}
          onMoveCadreUp={moveCadreUp}
          onMoveCadreDown={moveCadreDown}
          onUpdateOrder={updateCadreOrder}
          onSave={saveCadreOrder}
          onClose={() => setShowOrderModal(false)}
        />
      )}

      {/* Speakers Order Modal */}
      {showSpeakersOrderModal && selectedCadreForOrdering && (
        <SpeakersOrderModal
          cadre={selectedCadreForOrdering}
          selectedEdition={selectedEdition}
          onClose={() => {
            setShowSpeakersOrderModal(false);
            setSelectedCadreForOrdering(null);
          }}
          onSave={() => {
            setShowSpeakersOrderModal(false);
            setSelectedCadreForOrdering(null);
          }}
        />
      )}
    </>
  );
}

function EditCadreRow({ cadre, onSave, onCancel }) {
  const [editData, setEditData] = useState(cadre);

  return (
    <>
      <div style={{ 
        fontWeight: 'bold', 
        backgroundColor: '#f0f0f0', 
        padding: '4px 8px', 
        borderRadius: '4px',
        minWidth: '30px',
        textAlign: 'center'
      }}>
        {editData.display_order}
      </div>
      <div>
        <input 
          type="text" 
          value={editData.title}
          onChange={(e) => setEditData({...editData, title: e.target.value})}
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <button
          className="btn-secondary"
          style={{ padding: '4px 8px', fontSize: '12px' }}
          disabled
        >
          Gérer ordre
        </button>
      </div>
      <div className="actions">
        <button onClick={() => onSave(editData)} className="btn-primary">
          <HiCheck />
        </button>
        <button onClick={onCancel} className="btn-secondary">
          <HiX />
        </button>
      </div>
    </>
  );
}

// New modal component for managing cadre order
function CadreOrderModal({ cadres, onCadreOrderChange, onMoveCadreUp, onMoveCadreDown, onUpdateOrder, onSave, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>Réorganiser l'ordre des cadres</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Modifiez l'ordre d'affichage des cadres sur la page des intervenants.
          </p>
          <div className="cadre-order-list">
            {cadres.map((cadre, index) => (
              <div key={cadre.id} className="cadre-order-item" style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto auto',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    min="1"
                    max={cadres.length}
                    value={cadre.order}
                    onChange={(e) => onUpdateOrder(cadre.id, e.target.value)}
                    style={{ 
                      width: '60px', 
                      padding: '4px', 
                      textAlign: 'center',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div style={{ fontWeight: '500' }}>{cadre.title}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <button
                    onClick={() => onMoveCadreUp(index)}
                    disabled={index === 0}
                    className="btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    title="Monter"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => onMoveCadreDown(index)}
                    disabled={index === cadres.length - 1}
                    className="btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    title="Descendre"
                  >
                    ↓
                  </button>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#888',
                  fontWeight: 'bold',
                  minWidth: '40px',
                  textAlign: 'center'
                }}>
                  #{index + 1}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#e8f4fd', borderRadius: '6px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#1976d2' }}>
              💡 Astuce : Vous pouvez modifier directement le numéro d'ordre ou utiliser les flèches pour déplacer les cadres.
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={onSave} className="btn-primary">Sauvegarder l'ordre</button>
        </div>
      </div>
    </div>
  );
}

// New modal component for managing speaker order within a cadre
function SpeakersOrderModal({ cadre, selectedEdition, onClose, onSave }) {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSpeakersInCadre();
  }, [cadre.id]);

  const loadSpeakersInCadre = async () => {
    try {
      setLoading(true);
      const speakersData = await cadreConferenceService.getSpeakersInCadre(cadre.id, selectedEdition);
      setSpeakers(speakersData);
    } catch (error) {
      console.error('Error loading speakers in cadre:', error);
    } finally {
      setLoading(false);
    }
  };

  const moveSpeakerUp = (index) => {
    if (index === 0) return;
    const newSpeakers = [...speakers];
    [newSpeakers[index], newSpeakers[index - 1]] = [newSpeakers[index - 1], newSpeakers[index]];
    setSpeakers(newSpeakers);
  };

  const moveSpeakerDown = (index) => {
    if (index === speakers.length - 1) return;
    const newSpeakers = [...speakers];
    [newSpeakers[index], newSpeakers[index + 1]] = [newSpeakers[index + 1], newSpeakers[index]];
    setSpeakers(newSpeakers);
  };

  const handleSave = async () => {
    try {
      const speakerOrders = speakers.map((speaker, index) => ({
        speakerId: speaker.id,
        order: index + 1
      }));
      
      await cadreConferenceService.updateSpeakerOrdersInCadre(cadre.id, speakerOrders);
      onSave();
    } catch (error) {
      console.error('Error saving speaker orders:', error);
      alert('Erreur lors de la sauvegarde de l\'ordre des intervenants.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>Ordre des intervenants - {cadre.title}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div>Chargement...</div>
          ) : speakers.length === 0 ? (
            <div>Aucun intervenant dans ce cadre.</div>
          ) : (
            <div className="speakers-order-list">
              {speakers.map((speaker, index) => (
                <div key={speaker.id} className="speaker-order-item" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: 'bold', minWidth: '30px' }}>{index + 1}.</span>
                    <div>
                      <div style={{ fontWeight: '500' }}>{speaker.name}</div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {speaker.title} - {speaker.organization}
                      </div>
                      {speaker.type === 'online' && (
                        <span style={{ 
                          fontSize: '12px', 
                          backgroundColor: '#007bff', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '3px' 
                        }}>
                          ONLINE
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <button
                      onClick={() => moveSpeakerUp(index)}
                      disabled={index === 0}
                      className="btn-secondary"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveSpeakerDown(index)}
                      disabled={index === speakers.length - 1}
                      className="btn-secondary"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={handleSave} className="btn-primary">Sauvegarder l'ordre</button>
        </div>
      </div>
    </div>
  );
}

// Modal to show linked speakers
function SpeakersLinkedModal({ speakers, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h3>Speakers liés</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <HiX />
          </button>
        </div>
        <div className="modal-body">
          {speakers.length === 0 ? (
            <div>Aucun intervenant lié.</div>
          ) : (
            <ul style={{ paddingLeft: 20 }}>
              {speakers.map(speaker => (
                <li key={speaker.id}>
                   {speaker.name}
               
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default ConferencesManagement;
